import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { estudianteService, caseService, interventionService, userService } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  abierto: { label: "Abierto", color: "bg-secondary-100 text-secondary-700" },
  en_evaluacion: { label: "En Evaluación", color: "bg-yellow-100 text-yellow-700" },
  en_intervencion: { label: "En Intervención", color: "bg-primary-100 text-primary-700" },
  esperando_insumos: { label: "Esperando Insumos", color: "bg-orange-100 text-orange-700" },
  esperando_informe_docente: { label: "Esperando Informe Docente", color: "bg-orange-100 text-orange-700" },
  esperando_validacion: { label: "Esperando Validación", color: "bg-yellow-100 text-yellow-700" },
  informe_generado: { label: "Informe Generado", color: "bg-accent-100 text-accent-700" },
  enviado_tribunal: { label: "Enviado a Tribunal", color: "bg-green-100 text-green-700" },
  derivado_red_externa: { label: "Derivado a Red Externa", color: "bg-purple-100 text-purple-700" },
  en_seguimiento: { label: "En Seguimiento", color: "bg-blue-100 text-blue-700" },
  cerrado: { label: "Cerrado", color: "bg-background-200 text-foreground-500" },
  reabierto: { label: "Reabierto", color: "bg-red-100 text-red-700" },
};

export default function EstudianteDetallePage() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"casos" | "intervenciones" | "informes" | "documentos">("casos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [student, setStudent] = useState<any>(null);
  const [casos, setCasos] = useState<any[]>([]);
  const [allIntervenciones, setAllIntervenciones] = useState<any[]>([]);
  const [reportsForStudent, setReportsForStudent] = useState<any[]>([]);

  // Role permissions
  const [canManage, setCanManage] = useState(false);
  
  // Modal & Form States
  const [showModal, setShowModal] = useState(false);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    rut: "",
    primer_nombre: "",
    segundo_nombre: "",
    apellido_paterno: "",
    apellido_materno: "",
    curso: "",
    nivel: "",
    fecha_nacimiento: "",
    profesor_jefe_id: "",
    apoderado_nombre: "",
    apoderado_telefono: "",
    apoderado_correo: "",
  });

  async function loadData() {
    if (!studentId) return;
    try {
      setError("");
      const [studData, casesData, intData, usersData] = await Promise.all([
        estudianteService.list(),
        caseService.list(),
        interventionService.list(),
        userService.list().catch(() => [])
      ]);

      const studArr = Array.isArray(studData) ? studData : [];
      const foundStudent = studArr.find((s: any) => String(s.id) === String(studentId));
      setStudent(foundStudent || null);

      if (foundStudent) {
        setFormData({
          rut: foundStudent.rut || "",
          primer_nombre: foundStudent.primer_nombre || "",
          segundo_nombre: foundStudent.segundo_nombre || "",
          apellido_paterno: foundStudent.apellido_paterno || "",
          apellido_materno: foundStudent.apellido_materno || "",
          curso: foundStudent.curso || "",
          nivel: foundStudent.nivel || "Básica",
          fecha_nacimiento: foundStudent.fecha_nacimiento ? foundStudent.fecha_nacimiento.substring(0, 10) : "",
          profesor_jefe_id: foundStudent.profesor_jefe_id || "",
          apoderado_nombre: foundStudent.apoderado_nombre || "",
          apoderado_telefono: foundStudent.apoderado_telefono || "",
          apoderado_correo: foundStudent.apoderado_correo || "",
        });
      }

      const mappedCases = Array.isArray(casesData) ? casesData.map(mapDbCaseToFrontend) : [];
      const studentCases = mappedCases.filter((c: any) => String(c.studentId) === String(studentId))
        .sort((a: any, b: any) => new Date(b.fechaApertura).getTime() - new Date(a.fechaApertura).getTime());
      setCasos(studentCases);

      const mappedInts = Array.isArray(intData) ? intData.map(mapDbInterventionToFrontend) : [];
      const caseIds = new Set(studentCases.map((c: any) => c.id));
      setAllIntervenciones(mappedInts.filter((i: any) => caseIds.has(i.caseId))
        .sort((a: any, b: any) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()));

      if (Array.isArray(usersData)) {
        setTeachers(usersData.filter((u: any) => u.rol === "profesor_jefe" || u.rol === "profesor"));
      }

    } catch (err: any) {
      console.error("Error loading student detail:", err);
      setError("Error al cargar datos del estudiante desde Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Check role
    const stored = localStorage.getItem("convivencia_user");
    if (stored) {
      const user = JSON.parse(stored);
      setCanManage(user.rol === "admin" || user.rol === "director");
    }

    loadData();
  }, [studentId]);

  const handleOpenEdit = () => {
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.rut || !formData.primer_nombre || !formData.apellido_paterno || !formData.apellido_materno || !formData.curso || !formData.apoderado_nombre) {
      alert("Por favor completa los campos obligatorios: RUT, Primer Nombre, Apellidos, Curso y Apoderado.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const payload = {
        rut: formData.rut,
        primer_nombre: formData.primer_nombre,
        segundo_nombre: formData.segundo_nombre || null,
        apellido_paterno: formData.apellido_paterno,
        apellido_materno: formData.apellido_materno,
        curso: formData.curso,
        nivel: formData.nivel || null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        profesor_jefe_id: formData.profesor_jefe_id || null,
        apoderado_nombre: formData.apoderado_nombre,
        apoderado_telefono: formData.apoderado_telefono || null,
        apoderado_correo: formData.apoderado_correo || null,
      };

      await estudianteService.update(studentId!, payload);
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      console.error("Error saving student:", err);
      alert("Error al guardar estudiante: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de que deseas desactivar a este estudiante?")) {
      return;
    }
    try {
      setLoading(true);
      await estudianteService.delete(studentId!);
      alert("Estudiante desactivado exitosamente.");
      navigate("/estudiantes");
    } catch (err: any) {
      console.error("Error deactivating student:", err);
      alert("Error al desactivar estudiante: " + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  if (loading && !student) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando expediente...</p>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="p-6 text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-background-100">
          <i className="ri-user-search-line text-2xl text-foreground-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-900">Estudiante no encontrado</h2>
        <button onClick={() => navigate("/estudiantes")} className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">Volver a Estudiantes</button>
      </div>
    );
  }

  const casosActivos = casos.filter((c) => c.estado !== "cerrado");
  const casosCerrados = casos.filter((c) => c.estado === "cerrado");
  const judicializados = casos.filter((c) => c.judicializado && c.estado !== "cerrado");

  return (
    <div className="p-4 md:p-6 space-y-5">
      <button
        onClick={() => navigate("/estudiantes")}
        className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
      >
        <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-left-line text-sm" /></span>
        Volver a Estudiantes
      </button>

      {/* Student Header */}
      <div className="bg-white border border-background-200 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-primary-700">{student.nombre?.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground-950">{student.nombre}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-foreground-600">
              <span><strong className="text-foreground-700">RUT:</strong> {student.rut}</span>
              <span><strong className="text-foreground-700">Curso:</strong> {student.curso}</span>
              <span><strong className="text-foreground-700">Profesor Jefe:</strong> {student.profesorJefe || student.profesor_jefe_nombre || "-"}</span>
            </div>
            {student.apoderado_nombre && (
              <div className="mt-4 p-3.5 bg-background-50 rounded-xl border border-background-100 text-xs space-y-1">
                <p className="font-semibold text-foreground-900 flex items-center gap-1"><i className="ri-parent-line text-sm text-primary-500" /> Apoderado Responsable</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-foreground-600 mt-1.5">
                  <p><strong>Nombre:</strong> {student.apoderado_nombre}</p>
                  {student.apoderado_telefono && <p><strong>Teléfono:</strong> {student.apoderado_telefono}</p>}
                  {student.apoderado_correo && <p><strong>Correo:</strong> {student.apoderado_correo}</p>}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/casos/nuevo`)}
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Abrir Caso
            </button>
            {canManage && (
              <>
                <button
                  onClick={handleOpenEdit}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-semibold hover:bg-yellow-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Editar Ficha
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Desactivar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-background-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground-950">{casos.length}</p>
            <p className="text-xs text-foreground-500">Total Casos</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">{casosActivos.length}</p>
            <p className="text-xs text-foreground-500">Casos Activos</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">{judicializados.length}</p>
            <p className="text-xs text-foreground-500">Judicializados</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground-950">{allIntervenciones.length}</p>
            <p className="text-xs text-foreground-500">Intervenciones</p>
          </div>
        </div>
      </div>

      {/* Alertas activas */}
      {casosActivos.some((c) => c.prioridad === "critica") && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <span className="flex items-center justify-center w-6 h-6 flex-shrink-0 mt-0.5"><i className="ri-alert-fill text-red-500 text-lg" /></span>
          <div>
            <p className="text-sm font-semibold text-red-700">Alertas Activas</p>
            <ul className="mt-1 space-y-1 text-sm text-red-600">
              {casosActivos.filter((c) => c.prioridad === "critica").map((c) => (
                <li key={c.id} className="flex items-center gap-1.5 cursor-pointer hover:underline" onClick={() => navigate(`/casos/${c.id}`)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  {c.titulo} — {c.estado === "esperando_insumos" ? "Esperando insumos" : STATUS_CONFIG[c.estado]?.label || c.estado}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-background-200">
        {([
          { key: "casos", label: "Casos", count: casos.length, icon: "ri-folder-3-line" },
          { key: "intervenciones", label: "Intervenciones", count: allIntervenciones.length, icon: "ri-timeline-view" },
          { key: "informes", label: "Informes", count: reportsForStudent.length, icon: "ri-article-line" },
          { key: "documentos", label: "Documentos", count: 0, icon: "ri-file-text-line" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? "border-primary-500 text-primary-600" : "border-transparent text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <span className="flex items-center justify-center w-4 h-4"><i className={tab.icon} /></span>
            {tab.label}
            {tab.count > 0 && <span className="text-xs bg-background-100 px-1.5 py-0.5 rounded-full">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "casos" && (
        <div className="space-y-3">
          {casos.length === 0 ? (
            <div className="text-center py-12 text-foreground-400">
              <p className="text-sm">Sin casos registrados</p>
            </div>
          ) : (
            casos.map((caso) => {
              const sc = STATUS_CONFIG[caso.estado] || STATUS_CONFIG.abierto;
              return (
                <div
                  key={caso.id}
                  onClick={() => navigate(`/casos/${caso.id}`)}
                  className="bg-white border border-background-200 rounded-xl p-4 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-mono text-foreground-400">{caso.folio}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>{sc.label}</span>
                    {caso.judicializado && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Judicializado</span>}
                    <span className={`text-xs font-medium ml-auto ${
                      caso.prioridad === "critica" ? "text-red-600" : caso.prioridad === "alta" ? "text-orange-500" : "text-foreground-500"
                    }`}>{caso.prioridad.toUpperCase()}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900">{caso.titulo}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-500">
                    <span>Responsable: {caso.responsableName}</span>
                    <span>Apertura: {caso.fechaApertura}</span>
                    <span>Límite: {caso.fechaLimite}</span>
                    {caso.fechaCierre && <span>Cierre: {caso.fechaCierre}</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === "intervenciones" && (
        <div>
          {allIntervenciones.length === 0 ? (
            <div className="text-center py-12 text-foreground-400">
              <p className="text-sm">Sin intervenciones registradas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allIntervenciones.map((interv) => (
                <div key={interv.id} className={`bg-white border rounded-lg p-4 ${interv.confidencial ? "border-purple-200 bg-purple-50/30" : "border-background-200"}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-foreground-900">{interv.tipoIntervencion.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                    {interv.confidencial && <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 font-medium">Confidencial</span>}
                    <span className="text-xs text-foreground-400 ml-auto">{interv.fechaFormatted}</span>
                  </div>
                  <p className="text-sm text-foreground-700">{interv.descripcion}</p>
                  <p className="text-xs text-foreground-400 mt-1">Caso: {casos.find((c) => c.id === interv.caseId)?.folio || interv.caseId}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "informes" && (
        <div>
          {reportsForStudent.length === 0 ? (
            <div className="text-center py-12 text-foreground-400">
              <p className="text-sm">Sin informes generados</p>
            </div>
          ) : (
            reportsForStudent.map((r) => (
              <div key={r.id} className="bg-white border border-background-200 rounded-lg p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground-900">{r.tipoReporte}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    r.estado === "enviado" ? "bg-green-100 text-green-700" : r.estado === "aprobado" ? "bg-accent-100 text-accent-700" : "bg-yellow-100 text-yellow-700"
                  }`}>{r.estado}</span>
                </div>
                <div className="text-xs text-foreground-500">Generado: {r.createdAt} · Estado: {r.estado}</div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "documentos" && (
        <div className="text-center py-16 text-foreground-400">
          <span className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-file-text-line text-2xl text-foreground-300" />
          </span>
          <p className="text-sm font-medium">Sin documentos adjuntos</p>
        </div>
      )}

      {/* Modal Edición Estudiante */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Editar Ficha de Estudiante"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} className="cursor-pointer">Cancelar</Button>
            <Button onClick={handleSave} className="cursor-pointer bg-primary-500 text-white hover:bg-primary-600">
              Guardar Cambios
            </Button>
          </>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider">Datos Personales del Estudiante</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="RUT *"
              placeholder="12.345.678-9"
              value={formData.rut}
              onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
            />
            <Input
              label="Primer Nombre *"
              placeholder="Juan"
              value={formData.primer_nombre}
              onChange={(e) => setFormData({ ...formData, primer_nombre: e.target.value })}
            />
            <Input
              label="Segundo Nombre"
              placeholder="Andrés"
              value={formData.segundo_nombre}
              onChange={(e) => setFormData({ ...formData, segundo_nombre: e.target.value })}
            />
            <Input
              label="Apellido Paterno *"
              placeholder="Pérez"
              value={formData.apellido_paterno}
              onChange={(e) => setFormData({ ...formData, apellido_paterno: e.target.value })}
            />
            <Input
              label="Apellido Materno *"
              placeholder="González"
              value={formData.apellido_materno}
              onChange={(e) => setFormData({ ...formData, apellido_materno: e.target.value })}
            />
            <Input
              label="Fecha de Nacimiento"
              type="date"
              value={formData.fecha_nacimiento}
              onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
            />
          </div>

          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider pt-2">Datos Académicos</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Curso *"
              placeholder="1° Medio A"
              value={formData.curso}
              onChange={(e) => setFormData({ ...formData, curso: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nivel</label>
              <select
                value={formData.nivel}
                onChange={(e) => setFormData({ ...formData, nivel: e.target.value })}
                className="w-full text-sm rounded-lg border border-background-200 bg-white text-foreground-900 px-3 py-2 outline-none cursor-pointer"
              >
                <option value="Parvularia">Parvularia</option>
                <option value="Básica">Básica</option>
                <option value="Media">Media</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Profesor Jefe</label>
              <select
                value={formData.profesor_jefe_id}
                onChange={(e) => setFormData({ ...formData, profesor_jefe_id: e.target.value })}
                className="w-full text-sm rounded-lg border border-background-200 bg-white text-foreground-900 px-3 py-2 outline-none cursor-pointer"
              >
                <option value="">Seleccionar...</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider pt-2">Datos del Apoderado Responsable</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Input
                label="Nombre Completo del Apoderado *"
                placeholder="María José González"
                value={formData.apoderado_nombre}
                onChange={(e) => setFormData({ ...formData, apoderado_nombre: e.target.value })}
              />
            </div>
            <Input
              label="Teléfono del Apoderado"
              placeholder="+56 9 1234 5678"
              value={formData.apoderado_telefono}
              onChange={(e) => setFormData({ ...formData, apoderado_telefono: e.target.value })}
            />
            <Input
              label="Correo del Apoderado"
              type="email"
              placeholder="apoderado@correo.com"
              value={formData.apoderado_correo}
              onChange={(e) => setFormData({ ...formData, apoderado_correo: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}